<template>
  <Teleport to="body">
    <Transition name="ccm">
      <div
        v-if="modelValue"
        class="ccm-backdrop"
        role="presentation"
        @click.self="close"
      >
        <div
          :id="dialogId"
          class="ccm-dialog home-glass-surface"
          role="dialog"
          aria-modal="true"
          :aria-labelledby="titleId"
          aria-describedby="ccm-desc"
          @keydown.stop
        >
          <button
            ref="closeBtnRef"
            type="button"
            class="ccm-close"
            aria-label="Cerrar formulario"
            @click="close"
          >
            <i class="fas fa-times" aria-hidden="true" />
          </button>

          <div class="ccm-layout">
            <div class="ccm-visual" aria-hidden="true">
              <img :src="heroImage" alt="" class="ccm-visual__img" width="520" height="640" />
              <div class="ccm-visual__shade" />
              <p class="ccm-visual__tag">¿Necesitás ayuda?</p>
            </div>

            <div class="ccm-panel">
              <div class="ccm-panel__inner">
                <p :id="titleId" class="ccm-title">Consulta</p>
                <p id="ccm-desc" class="ccm-lead">
                  Dejanos tus datos y te respondemos por email a la brevedad.
                </p>

                <Transition name="ccm-fade">
                  <div v-if="submitted" class="ccm-success">
                    <div class="ccm-success__icon" aria-hidden="true">
                      <i class="fas fa-check" />
                    </div>
                    <h2 class="ccm-success__title">¡Listo!</h2>
                    <p class="ccm-success__text">
                      Recibimos tu consulta. Te escribiremos a
                      <strong>{{ form.email }}</strong> cuando podamos.
                    </p>
                    <button type="button" class="btn btn-primary ccm-btn-primary" @click="afterSuccessClose">
                      Cerrar
                    </button>
                  </div>
                </Transition>

                <form v-if="!submitted" class="ccm-form" novalidate @submit.prevent="handleSubmit">
                  <div class="row g-3">
                    <div class="col-sm-6">
                      <div class="ccm-field" :class="fieldClass('name')">
                        <label class="ccm-label" for="ccm-name">Nombre <span class="ccm-req">*</span></label>
                        <input
                          id="ccm-name"
                          v-model.trim="form.name"
                          type="text"
                          class="ccm-input"
                          autocomplete="name"
                          placeholder="Tu nombre"
                          @blur="touch('name')"
                        />
                        <span v-if="errors.name" class="ccm-error">{{ errors.name }}</span>
                      </div>
                    </div>
                    <div class="col-sm-6">
                      <div class="ccm-field" :class="fieldClass('email')">
                        <label class="ccm-label" for="ccm-email">Email <span class="ccm-req">*</span></label>
                        <input
                          id="ccm-email"
                          v-model.trim="form.email"
                          type="email"
                          class="ccm-input"
                          autocomplete="email"
                          placeholder="tu@email.com"
                          @blur="touch('email')"
                        />
                        <span v-if="errors.email" class="ccm-error">{{ errors.email }}</span>
                      </div>
                    </div>
                  </div>

                  <div class="ccm-field mt-1" :class="fieldClass('phone')">
                    <label class="ccm-label" for="ccm-phone">Teléfono o WhatsApp</label>
                    <input
                      id="ccm-phone"
                      v-model.trim="form.phone"
                      type="tel"
                      class="ccm-input"
                      autocomplete="tel"
                      placeholder="Opcional — nos ayuda a coordinar"
                      @blur="touch('phone')"
                    />
                    <span v-if="errors.phone" class="ccm-error">{{ errors.phone }}</span>
                  </div>

                  <div class="ccm-field mt-1" :class="fieldClass('subject')">
                    <label class="ccm-label" for="ccm-subject">Asunto <span class="ccm-req">*</span></label>
                    <input
                      id="ccm-subject"
                      v-model.trim="form.subject"
                      type="text"
                      class="ccm-input"
                      placeholder="Ej. Reparación de pantalla"
                      @blur="touch('subject')"
                    />
                    <span v-if="errors.subject" class="ccm-error">{{ errors.subject }}</span>
                  </div>

                  <div class="ccm-field mt-1" :class="fieldClass('message')">
                    <label class="ccm-label" for="ccm-message">
                      Mensaje <span class="ccm-req">*</span>
                      <span class="ccm-counter" :class="{ 'ccm-counter--warn': messageLength > 1800 }">
                        {{ messageLength }}/2000
                      </span>
                    </label>
                    <textarea
                      id="ccm-message"
                      v-model="form.message"
                      class="ccm-textarea"
                      rows="4"
                      maxlength="2000"
                      placeholder="Modelo del equipo, avería y cualquier detalle útil…"
                      @blur="touch('message')"
                    />
                    <span v-if="errors.message" class="ccm-error">{{ errors.message }}</span>
                  </div>

                  <p v-if="rateLimited" class="ccm-alert ccm-alert--warn" role="status">
                    <i class="fas fa-clock me-2" aria-hidden="true" />
                    Demasiados envíos. Probá de nuevo en <strong>{{ rateLimitCountdown }}</strong>.
                  </p>
                  <p v-if="generalError" class="ccm-alert ccm-alert--err" role="alert">
                    <i class="fas fa-exclamation-circle me-2" aria-hidden="true" />
                    {{ generalError }}
                  </p>

                  <button
                    type="submit"
                    class="btn btn-primary ccm-btn-primary mt-2"
                    :disabled="isLoading || rateLimited"
                  >
                    <span v-if="isLoading" class="ccm-btn-inner">
                      <span class="spinner-border spinner-border-sm" aria-hidden="true" />
                      Enviando…
                    </span>
                    <span v-else class="ccm-btn-inner">
                      <i class="fas fa-paper-plane" aria-hidden="true" />
                      Enviar consulta
                    </span>
                  </button>

                  <p class="ccm-foot">
                    <i class="fas fa-lock me-1" aria-hidden="true" />
                    Tus datos solo se usan para responder esta consulta.
                    <router-link to="/contacto" class="ccm-foot__link" @click="close">
                      Más opciones de contacto
                    </router-link>
                  </p>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, reactive, ref, watch } from 'vue';
import { sendContactMessage } from '@/services/contact';

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  /** Imagen del panel izquierdo */
  heroImage: { type: String, required: true },
  /** Texto inicial del asunto al abrir */
  defaultSubject: { type: String, default: '' },
  /** id del dialog para aria-controls en el disparador */
  dialogId: { type: String, default: 'contact-consult-dialog' },
});

const emit = defineEmits(['update:modelValue']);

const titleId = 'ccm-dialog-title';
const closeBtnRef = ref(null);
const form = reactive({
  name: '',
  email: '',
  phone: '',
  subject: '',
  message: '',
});
const errors = reactive({
  name: '',
  email: '',
  phone: '',
  subject: '',
  message: '',
});
const touched = reactive({
  name: false,
  email: false,
  phone: false,
  subject: false,
  message: false,
});

const isLoading = ref(false);
const submitted = ref(false);
const rateLimited = ref(false);
const generalError = ref('');
const rateLimitCountdown = ref('');
const RATE_LIMIT_KEY = 'contact_rl_until';

const messageLength = computed(() => form.message.length);

const fieldClass = (key) => ({
  'ccm-field--error': !!errors[key],
  'ccm-field--ok': touched[key] && !errors[key],
});

const close = () => {
  emit('update:modelValue', false);
};

const afterSuccessClose = () => {
  resetForm();
  close();
};

const startCountdown = (untilMs) => {
  const tick = () => {
    const diff = untilMs - Date.now();
    if (diff <= 0) {
      rateLimited.value = false;
      rateLimitCountdown.value = '';
      return;
    }
    const m = Math.floor(diff / 60000);
    const s = Math.floor((diff % 60000) / 1000);
    rateLimitCountdown.value = m > 0 ? `${m}m ${s}s` : `${s}s`;
    setTimeout(tick, 1000);
  };
  tick();
};

const applyRateLimitFromStorage = () => {
  const until = parseInt(localStorage.getItem(RATE_LIMIT_KEY) || '0', 10);
  if (until > Date.now()) {
    rateLimited.value = true;
    startCountdown(until);
  }
};

applyRateLimitFromStorage();

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const buildMessagePayload = () => {
  const phone = form.phone.trim();
  const base = form.message.trim();
  if (!phone) return base;
  return `Teléfono / WhatsApp: ${phone}\n\n${base}`;
};

const validate = () => {
  let ok = true;
  errors.name = form.name.length < 2 ? 'Al menos 2 caracteres.' : '';
  errors.email = !EMAIL_RE.test(form.email) ? 'Ingresá un email válido.' : '';
  const phTrim = form.phone.trim();
  errors.phone =
    phTrim && phTrim.length < 6 ? 'Si completás el teléfono, que sea al menos 6 caracteres.' : '';
  errors.subject = form.subject.length < 3 ? 'El asunto debe tener al menos 3 caracteres.' : '';

  const composed = buildMessagePayload();
  errors.message =
    composed.length < 10
      ? 'El mensaje debe tener al menos 10 caracteres.'
      : composed.length > 2000
        ? 'El texto total no puede superar los 2000 caracteres.'
        : '';

  if (errors.name || errors.email || errors.phone || errors.subject || errors.message) ok = false;
  return ok;
};

const touch = (field) => {
  touched[field] = true;
  if (field === 'name') errors.name = form.name.length < 2 ? 'Al menos 2 caracteres.' : '';
  if (field === 'email') errors.email = !EMAIL_RE.test(form.email) ? 'Ingresá un email válido.' : '';
  const ph = form.phone.trim();
  if (field === 'phone')
    errors.phone = ph && ph.length < 6 ? 'Si completás el teléfono, que sea al menos 6 caracteres.' : '';
  if (field === 'subject')
    errors.subject = form.subject.length < 3 ? 'El asunto debe tener al menos 3 caracteres.' : '';
  if (field === 'message') {
    const composed = buildMessagePayload();
    errors.message =
      composed.length < 10
        ? 'El mensaje debe tener al menos 10 caracteres.'
        : composed.length > 2000
          ? 'El texto total no puede superar los 2000 caracteres.'
          : '';
  }
};

let prevOverflow = '';

const onDocKeydown = (e) => {
  if (e.key === 'Escape' && props.modelValue) {
    e.preventDefault();
    close();
  }
};

watch(
  () => props.modelValue,
  async (open) => {
    if (open) {
      prevOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      resetForm();
      if (props.defaultSubject) form.subject = props.defaultSubject;
      generalError.value = '';
      await nextTick();
      closeBtnRef.value?.focus();
      window.addEventListener('keydown', onDocKeydown);
    } else {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onDocKeydown);
    }
  },
);

onBeforeUnmount(() => {
  window.removeEventListener('keydown', onDocKeydown);
  document.body.style.overflow = prevOverflow;
});

const handleSubmit = async () => {
  Object.keys(touched).forEach((k) => {
    touched[k] = true;
  });
  if (!validate()) return;

  generalError.value = '';
  isLoading.value = true;

  try {
    await sendContactMessage({
      name: form.name,
      email: form.email,
      subject: form.subject,
      message: buildMessagePayload(),
    });
    submitted.value = true;
  } catch (err) {
    const status = err?.response?.status;
    if (status === 429) {
      rateLimited.value = true;
      const untilMs = Date.now() + 60 * 60 * 1000;
      localStorage.setItem(RATE_LIMIT_KEY, String(untilMs));
      startCountdown(untilMs);
    } else if (status === 400 && err?.response?.data?.errors) {
      Object.assign(errors, err.response.data.errors);
    } else {
      generalError.value =
        err?.response?.data?.error || 'No se pudo enviar. Intentá más tarde.';
    }
  } finally {
    isLoading.value = false;
  }
};

const resetForm = () => {
  Object.assign(form, { name: '', email: '', phone: '', subject: '', message: '' });
  Object.assign(errors, { name: '', email: '', phone: '', subject: '', message: '' });
  Object.assign(touched, { name: false, email: false, phone: false, subject: false, message: false });
  submitted.value = false;
  generalError.value = '';
};
</script>

<style scoped>
.ccm-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1080;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: max(1rem, env(safe-area-inset-top)) max(1rem, env(safe-area-inset-right))
    max(1rem, env(safe-area-inset-bottom)) max(1rem, env(safe-area-inset-left));
  background: rgba(0, 0, 0, 0.72);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
}

.ccm-dialog {
  position: relative;
  width: min(960px, 100%);
  max-height: min(640px, calc(100vh - 2rem));
  border-radius: 20px;
  overflow: hidden;
  border: 1px solid var(--home-glass-border);
  box-shadow: var(--home-glass-shadow), 0 32px 80px rgba(0, 0, 0, 0.55);
  background: rgba(18, 18, 18, 0.94);
}

.ccm-close {
  position: absolute;
  top: 0.85rem;
  right: 0.85rem;
  z-index: 5;
  width: 40px;
  height: 40px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.18);
  background: rgba(0, 0, 0, 0.35);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition:
    background 0.2s ease,
    border-color 0.2s ease,
    transform 0.2s ease;
}

.ccm-close:hover {
  background: rgba(220, 38, 38, 0.35);
  border-color: rgba(252, 165, 165, 0.45);
  transform: scale(1.04);
}

.ccm-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1.15fr);
  min-height: min(520px, calc(100vh - 6rem));
  max-height: min(640px, calc(100vh - 2rem));
}

.ccm-visual {
  position: relative;
  min-height: 220px;
  overflow: hidden;
}

.ccm-visual__img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
}

.ccm-visual__shade {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    135deg,
    rgba(0, 0, 0, 0.15) 0%,
    rgba(0, 0, 0, 0.55) 55%,
    rgba(127, 29, 29, 0.35) 100%
  );
  pointer-events: none;
}

.ccm-visual__tag {
  position: absolute;
  left: 1.25rem;
  bottom: 1.25rem;
  margin: 0;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.88);
  text-shadow: 0 2px 12px rgba(0, 0, 0, 0.65);
}

.ccm-panel {
  display: flex;
  flex-direction: column;
  min-height: 0;
  min-width: 0;
  background: linear-gradient(165deg, rgba(28, 28, 28, 0.98) 0%, rgba(12, 12, 12, 0.99) 100%);
  border-left: 1px solid rgba(255, 255, 255, 0.06);
}

.ccm-panel__inner {
  padding: clamp(1.35rem, 3vw, 2rem);
  overflow-y: auto;
  flex: 1;
  scrollbar-gutter: stable;
}

.ccm-title {
  margin: 0 0 0.35rem;
  font-size: clamp(1.35rem, 2.5vw, 1.65rem);
  font-weight: 800;
  letter-spacing: -0.02em;
  color: #fff;
}

.ccm-lead {
  margin: 0 0 1.25rem;
  font-size: 0.88rem;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.58);
}

.ccm-field {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.ccm-label {
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: rgba(255, 255, 255, 0.52);
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 0.5rem;
}

.ccm-req {
  color: var(--primary-color);
  font-weight: 800;
}

.ccm-counter {
  font-size: 0.65rem;
  font-weight: 600;
  text-transform: none;
  letter-spacing: 0;
  color: rgba(255, 255, 255, 0.35);
}

.ccm-counter--warn {
  color: #fbbf24;
}

.ccm-input,
.ccm-textarea {
  width: 100%;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.14);
  background: rgba(255, 255, 255, 0.06);
  color: #fff;
  font-size: 0.9rem;
  padding: 0.62rem 0.85rem;
  outline: none;
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease,
    background 0.2s ease;
}

.ccm-input::placeholder,
.ccm-textarea::placeholder {
  color: rgba(255, 255, 255, 0.32);
}

.ccm-input:focus,
.ccm-textarea:focus {
  border-color: rgba(220, 38, 38, 0.55);
  background: rgba(255, 255, 255, 0.08);
  box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.18);
}

.ccm-textarea {
  resize: vertical;
  min-height: 108px;
  font-family: inherit;
  line-height: 1.45;
}

.ccm-field--error .ccm-input,
.ccm-field--error .ccm-textarea {
  border-color: rgba(248, 113, 113, 0.65);
  background: rgba(127, 29, 29, 0.15);
}

.ccm-field--ok .ccm-input,
.ccm-field--ok .ccm-textarea {
  border-color: rgba(52, 211, 153, 0.45);
}

.ccm-error {
  font-size: 0.72rem;
  color: #fca5a5;
  font-weight: 500;
}

.ccm-alert {
  margin: 0;
  padding: 0.65rem 0.85rem;
  border-radius: 12px;
  font-size: 0.82rem;
  font-weight: 500;
}

.ccm-alert--warn {
  background: rgba(120, 53, 15, 0.35);
  border: 1px solid rgba(251, 191, 36, 0.35);
  color: #fde68a;
}

.ccm-alert--err {
  background: rgba(127, 29, 29, 0.35);
  border: 1px solid rgba(248, 113, 113, 0.35);
  color: #fecaca;
}

.ccm-btn-primary {
  width: 100%;
  border-radius: 12px !important;
  font-weight: 700 !important;
  padding: 0.72rem 1rem !important;
  letter-spacing: 0.02em;
}

.ccm-btn-inner {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.ccm-foot {
  margin: 0.85rem 0 0;
  font-size: 0.72rem;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.42);
  text-align: center;
}

.ccm-foot__link {
  display: block;
  margin-top: 0.35rem;
  color: rgba(252, 165, 165, 0.95);
  font-weight: 600;
  text-decoration: none;
}

.ccm-foot__link:hover {
  text-decoration: underline;
  color: #fff;
}

.ccm-success {
  text-align: center;
  padding: 0.5rem 0 0.25rem;
}

.ccm-success__icon {
  width: 56px;
  height: 56px;
  margin: 0 auto 1rem;
  border-radius: 50%;
  background: rgba(220, 38, 38, 0.2);
  border: 1px solid rgba(252, 165, 165, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.35rem;
  color: #fca5a5;
}

.ccm-success__title {
  margin: 0 0 0.5rem;
  font-size: 1.25rem;
  font-weight: 800;
  color: #fff;
}

.ccm-success__text {
  margin: 0 0 1.25rem;
  font-size: 0.88rem;
  color: rgba(255, 255, 255, 0.62);
  line-height: 1.55;
}

.ccm-enter-active,
.ccm-leave-active {
  transition: opacity 0.28s ease;
}

.ccm-enter-active .ccm-dialog,
.ccm-leave-active .ccm-dialog {
  transition:
    transform 0.32s cubic-bezier(0.34, 1.2, 0.64, 1),
    opacity 0.28s ease;
}

.ccm-enter-from,
.ccm-leave-to {
  opacity: 0;
}

.ccm-enter-from .ccm-dialog,
.ccm-leave-to .ccm-dialog {
  opacity: 0;
  transform: translateY(14px) scale(0.98);
}

.ccm-fade-enter-active,
.ccm-fade-leave-active {
  transition: opacity 0.25s ease;
}

.ccm-fade-enter-from,
.ccm-fade-leave-to {
  opacity: 0;
}

@media (max-width: 767.98px) {
  .ccm-layout {
    grid-template-columns: 1fr;
    grid-template-rows: minmax(140px, 26vh) minmax(0, 1fr);
    max-height: calc(100vh - 2rem);
    min-height: 0;
  }

  .ccm-visual__tag {
    left: 1rem;
    bottom: 1rem;
    font-size: 0.68rem;
  }

  .ccm-panel {
    border-left: none;
    border-top: 1px solid rgba(255, 255, 255, 0.06);
  }

  .ccm-dialog {
    max-height: calc(100vh - 2rem);
  }
}

@media (prefers-reduced-motion: reduce) {
  .ccm-enter-active,
  .ccm-leave-active,
  .ccm-enter-active .ccm-dialog,
  .ccm-leave-active .ccm-dialog {
    transition: none;
  }
}
</style>
